/**
 * Terms and Privacy, rendered from the API.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../components/Icon';
import { EmptyState, Notice } from '../components/ui';
import { api } from '../lib/api';
import { useHashScroll } from '../lib/useHashScroll';

export default function Legal({ slug }) {
  const { t } = useTranslation();
  const [doc, setDoc] = useState(null);
  const [isDraft, setIsDraft] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setDoc(null);
    api
      .legal(slug, controller.signal)
      .then((payload) => {
        setDoc(payload.document);
        setIsDraft(payload.isDraft);
      })
      .catch((err) => err.name !== 'AbortError' && setError(err.message));
    return () => controller.abort();
  }, [slug]);

  useHashScroll(Boolean(doc));

  if (error) return <div className="shell section"><EmptyState icon="alert" title={t('legal.loadError', 'Could not load document')}>{error}</EmptyState></div>;
  if (!doc) return <div className="shell section"><div className="skeleton" style={{ height: 400, borderRadius: 'var(--radius-lg)' }} /></div>;

  return (
    <div className="shell" style={{ paddingBlock: 'var(--space-3xl) var(--space-5xl)' }}>
      <header className="stack" style={{ marginBottom: 'var(--space-3xl)', maxWidth: '60ch' }}>
        {doc.tibetanTitle && (
          <p className="tibetan tibetan--gold" style={{ fontSize: 24 }} lang="bo">{doc.tibetanTitle}</p>
        )}
        <h1 className="display display--lg">{doc.title}</h1>
        <p className="lede">{doc.subtitle}</p>
        <p className="subtle">{t('legal.effectiveDate', { date: doc.effective, defaultValue: `Effective ${doc.effective}` })}</p>
      </header>

      {isDraft && (
        <div style={{ marginBottom: 'var(--space-3xl)', maxWidth: '70ch' }}>
          <Notice tone="warn" icon="alert" title={t('legal.draftNoticeTitle', 'This is a draft')}>
            {t('legal.draftNoticeBody', 'It is written for what GOMPA actually does and how offerings are handled respectfully.')}
          </Notice>
        </div>
      )}

      <div className="legal-layout">
        <nav className="legal-toc" aria-label={t('legal.tocLabel', 'On this page')}>
          {doc.sections.map((section) => (
            <a key={section.id} href={`#${section.id}`}>{section.heading}</a>
          ))}
        </nav>

        <div className="prose">
          <p className="lede" style={{ marginBottom: 'var(--space-2xl)' }}>{doc.intro}</p>

          {doc.sections.map((section) => (
            <section key={section.id}>
              <h2 id={section.id}>{section.heading}</h2>
              {section.body?.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}

              {section.groups?.map((group) => (
                <div className="clause-group" key={group.title}>
                  <h3>{group.title}</h3>
                  <ul>
                    {group.items.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}

              {section.trailing?.map((paragraph, index) => (
                <p key={`t${index}`}>{paragraph}</p>
              ))}
              {section.pending && (
                <div className="pending-clause">
                  <b>
                    <Icon name="alert" size={12} style={{ verticalAlign: -2, marginRight: 5 }} />
                    {t('legal.pendingLabel', 'Not yet decided')}
                  </b>
                  <span>{section.pending}</span>
                </div>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
